import { Reveal, SectionTag } from "./shared";
import { ShoppingCart, Boxes, Factory, Handshake } from "lucide-react";

const functions = [
  { icon: ShoppingCart, name: "Procurement", text: "minimizes unit cost and over-orders to lock in bulk pricing." },
  { icon: Boxes, name: "Inventory", text: "keeps oversized safety buffers to prevent stockouts." },
  { icon: Factory, name: "Operations", text: "runs large batch campaigns for efficiency, leaving cash trapped in WIP." },
  { icon: Handshake, name: "Sales", text: "grants lenient terms to win customers, delaying collections." },
];

export const Problem = () => (
  <section id="problem" className="py-12 lg:py-16 bg-white" data-testid="problem-section">
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <SectionTag num="01" label="The Problem" />
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-display font-extrabold text-[#0B1B2B] tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-[44px]">
              Your operating partners are optimizing the <span className="text-[#3B7BC8]">wrong metric.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[#3c4a58] leading-relaxed text-base md:text-lg">
              Each function owns a single goal. Individually, each decision is defensible. Collectively,
              they scatter your working capital across the balance sheet — and no single owner is
              accountable for total capital efficiency.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 p-7 rounded-2xl bg-[#0B1B2B] text-white" data-testid="problem-result-card">
              <p className="text-[13px] uppercase tracking-[0.18em] font-bold text-[#7fd6b8] mb-3">The Result</p>
              <p className="font-display text-2xl md:text-3xl font-extrabold leading-snug">
                Our experience has shown that between 4 to 12% of revenue is recoverable — cash your ops team cannot see.
              </p>
              <p className="mt-4 text-white/80 text-sm leading-relaxed">
                Because no one is measuring the interaction between departments. Your diligence didn't catch it —
                ops diagnostics measure performance <em>within</em> functions, not <em>between</em> them.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5 content-start">
          {functions.map((f, i) => (
            <Reveal key={f.name} delay={0.08 * i}>
              <div className="group h-full p-7 rounded-2xl border border-[#0B1B2B]/10 bg-[#f7f9fa] hover:bg-white hover:shadow-[0_20px_50px_rgba(11,27,43,0.1)] hover:-translate-y-1 transition-all duration-500" data-testid={`problem-card-${f.name.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-white border border-[#0B1B2B]/10 flex items-center justify-center group-hover:bg-[#0E5F46] transition-colors duration-500">
                  <f.icon className="w-5 h-5 text-[#0E5F46] group-hover:text-white transition-colors duration-500" strokeWidth={2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-[#0B1B2B] text-lg">{f.name}</h3>
                <p className="mt-2 text-[#3c4a58] text-sm leading-relaxed">{f.text}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3} className="sm:col-span-2">
            <img
              src="/images/ops-floor.jpg"
              alt="Operations team reviewing inventory on a manufacturing floor"
              loading="lazy"
              data-testid="problem-ops-photo"
              className="w-full h-[240px] lg:h-[300px] object-cover rounded-2xl border border-[#0B1B2B]/10"
            />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
